// src/pages/finance/transaction/AddTransactionEntry.jsx
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";
import { Container, Row, Col, Card, Button, Form } from "react-bootstrap";
import Select from "react-select";
import ReactDOMServer from "react-dom/server";
import PrintableTransactionReceipt from "../../../PrintableTransactionReceipt";

function AddTransactionEntry() {
  const userData = sessionStorage.getItem("user");
  const userObj = userData ? JSON.parse(userData) : {};
  const baseUrl = process.env.REACT_APP_API_BASE_URL;

  const navigate = useNavigate();
  const routeLocation = useLocation();

  const [editId, setEditId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const todayStr = useMemo(() => new Date().toISOString().slice(0, 10), []);

  // ✅ Account Heads
  const [accountHeads, setAccountHeads] = useState([]);
  const [isAccountsLoading, setIsAccountsLoading] = useState(false);

  // ✅ Payment Modes
  const [paymentModes, setPaymentModes] = useState([]);
  const [isPaymentModesLoading, setIsPaymentModesLoading] = useState(false);
  const [schoolDetails, setSchoolDetails] = useState(null);

  const [form, setForm] = useState({
    transaction_id: 0,
    transaction_date: todayStr,

    account_head_id: "",
    account_head_code: "",

    amount: "",
    txn_type: "",

    payment_mode_id: "",

    reference_no: "",
    description: "",

    // ✅ Subledger checkbox
    is_subledger: false,
  });

  // ✅ Prefill edit mode
  useEffect(() => {
    if (routeLocation.state?.TransactionData) {
      const r = routeLocation.state.TransactionData;

      setForm((f) => ({
        ...f,
        transaction_id: r.transaction_id || 0,
        transaction_date: (r.transaction_date || todayStr).slice(0, 10),

        account_head_id: r.account_head_id || "",
        account_head_code: r.account_head_code || "",

        amount: r.amount ?? "",
        txn_type: (r.txn_type || "DEBIT").toUpperCase(),

        payment_mode_id: r.payment_mode_id || "",

        reference_no: r.reference_no || "",
        description: r.description || "",

        is_subledger: !!r.is_subledger,
      }));

      setEditId(r.transaction_id || 0);
    }
  }, [routeLocation, todayStr]);

const fetchAccountHeads = async () => {
  try {
    setIsAccountsLoading(true);
    const resp = await axios.get(`${baseUrl}/account-head/`, {
      params: { school_id: userObj.school_id },
    });

    // ✅ Only Active records
    setAccountHeads(
      (Array.isArray(resp.data) ? resp.data : []).filter(
        (acc) => acc.is_active === "Active"
      )
    );
  } catch (e) {
    console.error("Error fetching account heads:", e);
  } finally {
    setIsAccountsLoading(false);
  }
};

  const fetchPaymentModes = async () => {
    try {
      setIsPaymentModesLoading(true);
      const resp = await axios.get(`${baseUrl}/payment-modes/`, {
        params: { school_id: userObj.school_id }, // remove if not required
      });
      setPaymentModes(Array.isArray(resp.data) ? resp.data : []);
    } catch (e) {
      console.error("Error fetching payment modes:", e);

      setPaymentModes([]);
    } finally {
      setIsPaymentModesLoading(false);
    }
  };

  const fetchSchoolDetails = async () => {
    try {
      const resp = await axios.post(`${baseUrl}/schoolmaster/`, {
        action: "READ",
        school_id: userObj.school_id,
      });
      if (Array.isArray(resp.data) && resp.data.length > 0) {
        setSchoolDetails(resp.data[0]);
      }
    } catch (e) {
      console.error("Error fetching school details:", e);
    }
  };

  useEffect(() => {
    fetchAccountHeads();
    fetchPaymentModes();
    fetchSchoolDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const accountHeadOptions = useMemo(() => {
    return (accountHeads || []).map((x) => ({
      
      value: x.account_head_id ?? x.account_id ?? x.node_id ?? x.id,
      label: x.account_head_name ?? x.account_name ?? x.node_name ?? x.name,
      code: x.account_head_code ?? "",
    }));
  }, [accountHeads]);

  const paymentModeOptions = useMemo(() => {
    return (paymentModes || []).map((pm) => ({
      value: pm.payment_mode_id,
      label: (pm.payment_mode_name && (pm.payment_mode_name.toLowerCase() === "dd" || pm.payment_mode_name.toLowerCase() === "d.d")) ? "UPI" : pm.payment_mode_name,
    }));
  }, [paymentModes]);

  const txnTypeOptions = useMemo(
    () => [
      { value: "DEBIT", label: "Debit" },
      { value: "CREDIT", label: "Credit" },
    ],
    []
  );
  useEffect(() => {
    // when editing, once accountHeadOptions are available, derive code from selected account_head_id
    if (!editId) return;
    if (!form.account_head_id) return;
    if (!accountHeadOptions.length) return;

    const selected = accountHeadOptions.find(
      (o) => String(o.value) === String(form.account_head_id)
    );

    if (selected && selected.code !== form.account_head_code) {
      setForm((f) => ({
        ...f,
        account_head_code: selected.code || "",
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId, form.account_head_id, accountHeadOptions]);

  const selectedAccountHeadOption = useMemo(() => {
    if (!form.account_head_id) return null;
    return (
      accountHeadOptions.find((o) => String(o.value) === String(form.account_head_id)) || null
    );
  }, [form.account_head_id, accountHeadOptions]);

  const selectedPaymentModeOption = useMemo(() => {
    if (!form.payment_mode_id) return null;
    return (
      paymentModeOptions.find((o) => String(o.value) === String(form.payment_mode_id)) || null
    );
  }, [form.payment_mode_id, paymentModeOptions]);

  const validate = () => {
    if (!form.transaction_date) return "Date is required";
    if (!form.account_head_id) return "Account Head is required";

    if (form.amount !== "" && form.amount !== null && form.amount !== undefined) {
      const amt = Number(form.amount);
      if (Number.isNaN(amt) || amt < 0) return "Amount must be >= 0";
    }

    if (!form.txn_type) return "Transaction Type is required";
    if (!form.payment_mode_id) return "Payment Mode is required";
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const err = validate();
    if (err) return toast.error(err);

    try {
      setIsSaving(true);

      const payload = {
        transaction_id: editId || 0,
        school_id: userObj.school_id,
        transaction_date: form.transaction_date,

        account_head_id: Number(form.account_head_id),
        amount: form.amount === "" || form.amount === null ? 0 : Number(form.amount),
        txn_type: form.txn_type,

        payment_mode_id: Number(form.payment_mode_id),
        reference_no: form.reference_no?.trim() || null,
        description: form.description?.trim() || null,

        // ✅ Subledger value
        is_subledger: Boolean(form.is_subledger),

        is_active: true,
        user: userObj.username || userObj.user_name || "admin",
      };

      const response = await axios.post(`${baseUrl}/transaction_create_update/`, payload, {
        headers: { "Content-Type": "application/json" },
      });

      if (response.data?.status === "error" || response.data?.status === "Error") {
        toast.error(response.data?.message || "Something went wrong.");
        return;
      }

      toast.success(editId ? "Transaction Updated Successfully" : "Transaction Saved Successfully");

      const savedTransactionId = 
        response?.data?.data?.[0]?.transaction_entry_create_update || 
        response?.data?.transaction_id || 
        response?.data?.id || 
        editId || 
        "New";
      const printForm = { ...form };
      const accountHeadName = selectedAccountHeadOption?.label || "";
      const paymentModeName = selectedPaymentModeOption?.label || "";

      const printableHTML = ReactDOMServer.renderToString(
        <PrintableTransactionReceipt
          form={printForm}
          accountHeadName={accountHeadName}
          paymentModeName={paymentModeName}
          transactionId={savedTransactionId}
          schoolDetails={schoolDetails}
        />
      );

      const printWindow = window.open("", "_blank", "width=800,height=600");
      printWindow.document.write(`
        <html>
          <head>
            <title>Payment Voucher</title>
            <style>
              @page {
                size: A5 landscape;
                margin: 5mm;
              }
              body { 
                margin: 0; 
                padding: 0; 
                font-family: Arial, sans-serif;
                -webkit-print-color-adjust: exact;
              }
            </style>
          </head>
          <body>
            ${printableHTML}
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.focus();

      // Delay printing slightly to allow the school logo image to load
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 500);

      // reset
      setEditId(null);
      setForm({
        transaction_id: 0,
        transaction_date: todayStr,
        account_head_id: "",
        account_head_code: "",
        amount: "",
        txn_type: "",
        payment_mode_id: "",
        reference_no: "",
        description: "",
        is_subledger: false,
      });
    } catch (error) {
      console.error("Submit error:", error);
      const errMsg = error?.response?.data?.message || error?.response?.data?.error || "Server error";
      toast.error(errMsg);
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setEditId(null);
    setForm({
      transaction_id: 0,
      transaction_date: todayStr,
      account_head_id: "",
      account_head_code: "",
      amount: "",
      txn_type: "",
      payment_mode_id: "",
      reference_no: "",
      description: "",
      is_subledger: false,
    });
  };

  return (
    <div className="pageBody">
      <ToastContainer />
      <Container fluid>
        <Card>
          <Card.Body>
            <form onSubmit={handleSubmit}>
              <Row>
                <Col xs={12}>
                  <h6 className="commonSectionTitle">
                    {editId ? "Edit Transaction Entry" : "Transaction Entry Details"}
                  </h6>
                </Col>
              </Row>

              <Row>
                {/* Date */}
                <Col xs={12} md={6} lg={4} xxl={3}>
                  <div className="commonInput">
                    <Form.Group>
                      <Form.Label>
                        Date <span className="requiredStar">*</span>
                      </Form.Label>
                      <Form.Control
                        required
                        type="date"
                        value={form.transaction_date}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, transaction_date: e.target.value }))
                        }
                      />
                    </Form.Group>
                  </div>
                </Col>

                {/* Account Head */}
                <Col xs={12} md={6} lg={4} xxl={3}>
                  <div className="commonInput">
                    <Form.Group>
                      <Form.Label>
                        Account Head <span className="requiredStar">*</span>
                      </Form.Label>

                      <Select
                        inputId="account_head_id"
                        classNamePrefix="react-select"
                        placeholder={isAccountsLoading ? "Loading..." : "Select Account Head"}
                        isSearchable
                        isClearable
                        isLoading={isAccountsLoading}
                        options={accountHeadOptions}
                        value={selectedAccountHeadOption}
                        onChange={(opt) => {
                          setForm((f) => ({
                            ...f,
                            account_head_id: opt ? opt.value : "",
                            account_head_code: opt ? opt.code : "",
                          }));
                        }}
                      />
                    </Form.Group>
                  </div>
                </Col>

                {/* Account Head Code */}
                <Col xs={12} md={6} lg={4} xxl={3}>
                  <div className="commonInput">
                    <Form.Group>
                      <Form.Label>Account Head Code</Form.Label>
                      <Form.Control
                        type="text"
                        value={form.account_head_code}
                        placeholder="Auto"
                        disabled
                        readOnly
                      />
                    </Form.Group>
                  </div>
                </Col>

                {/* Amount */}
                <Col xs={12} md={6} lg={4} xxl={3}>
                  <div className="commonInput">
                    <Form.Group>
                      <Form.Label>
                        Amount <span className="requiredStar"></span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        inputMode="decimal"
                        placeholder="Enter Amount"
                        value={form.amount}
                        onChange={(e) => {
                          const value = e.target.value;

                          if (value === "" || /^\d*\.?\d{0,2}$/.test(value)) {
                            setForm((f) => ({
                              ...f,
                              amount: value,
                            }));
                          }
                        }}
                        onKeyDown={(e) => {
                          if (["-", "+", "e", "E"].includes(e.key)) {
                            e.preventDefault();
                          }
                        }}
                        onPaste={(e) => {
                          const pasted = e.clipboardData.getData("text");
                          if (!/^\d*\.?\d{0,2}$/.test(pasted)) {
                            e.preventDefault();
                          }
                        }}
                      />
                    </Form.Group>
                  </div>
                </Col>

                {/* Txn Type */}
                <Col xs={12} md={6} lg={4} xxl={3}>
                  <div className="commonInput">
                    <Form.Group>
                      <Form.Label>
                        Transaction Type <span className="requiredStar">*</span>
                      </Form.Label>
                      <Select
                        inputId="txn_type"
                        classNamePrefix="react-select"
                        placeholder="Select Transaction Type"
                        isSearchable
                        isClearable={false}
                        value={txnTypeOptions.find((o) => o.value === form.txn_type) || null}
                        onChange={(opt) =>
                          setForm((f) => ({ ...f, txn_type: opt?.value || "DEBIT" }))
                        }
                        options={txnTypeOptions}
                      />
                    </Form.Group>
                  </div>
                </Col>

                {/* Payment Mode */}
                <Col xs={12} md={6} lg={4} xxl={3}>
                  <div className="commonInput">
                    <Form.Group>
                      <Form.Label>
                        Payment Mode <span className="requiredStar">*</span>
                      </Form.Label>
                      <Select
                        inputId="payment_mode_id"
                        classNamePrefix="react-select"
                        placeholder={isPaymentModesLoading ? "Loading..." : "Select Payment Mode"}
                        isSearchable
                        isClearable
                        isLoading={isPaymentModesLoading}
                        options={paymentModeOptions}
                        value={selectedPaymentModeOption}
                        onChange={(opt) =>
                          setForm((f) => ({
                            ...f,
                            payment_mode_id: opt ? opt.value : "",
                          }))
                        }
                      />
                    </Form.Group>
                  </div>
                </Col>


                <Col xs={12} md={6} lg={4} xxl={3}>
                  <div className="commonInput">
                    <Form.Group>
                      <Form.Label>Reference No</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter Reference No"
                        maxLength={60}
                        value={form.reference_no}
                        onChange={(e) => setForm((f) => ({ ...f, reference_no: e.target.value }))}
                      />
                    </Form.Group>
                  </div>
                </Col>

                <Col xs={12} md={6} lg={4} xxl={3}>
                  <div className="commonInput">
                    <Form.Group controlId="is_subledger">
                      <Form.Label>Subledger</Form.Label>

                      <div className="d-flex align-items-center mt-2">
                        <Form.Check
                          type="checkbox"
                          id="is_subledger"
                          label="Enable Subledger"
                          checked={form.is_subledger}
                          onChange={(e) =>
                            setForm((f) => ({
                              ...f,
                              is_subledger: e.target.checked,
                            }))
                          }
                        />
                      </div>
                    </Form.Group>
                  </div>
                </Col>
                {/* Description */}
                <Col xs={12} md={12} lg={8} xxl={6}>
                  <div className="commonInput">
                    <Form.Group>
                      <Form.Label>Description</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={1}
                        placeholder="Enter Description"
                        maxLength={200}
                        value={form.description}
                        onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                      />
                    </Form.Group>
                  </div>
                </Col>
              </Row>

              <div className="d-flex justify-content-between mt-3">
                <div>
                  <Button
                    type="button"
                    variant="primary"
                    className="btn-info clearBtn"
                    onClick={handleReset}
                  >
                    Reset
                  </Button>
                </div>

                <div>
                  <Button
                    type="button"
                    variant="primary"
                    className="btn-danger secondaryBtn me-2"
                    onClick={() => navigate(-1)}
                  >
                    Cancel
                  </Button>

                  <Button
                    type="submit"
                    variant="primary"
                    className="btn-success primaryBtn"
                  >Submit
                  </Button>
                </div>
              </div>
            </form>
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
}

export default AddTransactionEntry;